import React, { useContext, useMemo } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { NumericFormat } from 'react-number-format';
import { formatDate } from '../../utils/time.util';
import { OrderContractDetailsProps } from './OrderContractDetails.interface';
import { Calendar } from '../../assets/Calendar';
import { Person } from '../../assets/Person';
import Flag from 'react-world-flags';
import ProducerCompanyIcon from '../../assets/ProducerCompanyIcon';
import DashedDivider from '../DashedDivider/DashedDivider';
import { AdminContracts } from '../../assets/Contracts/AdminContracts';
import { useNavigate } from 'react-router-dom';
import { OrderDetailsContext } from '../../context/OrderDetailsContext/order-details.context';
import { numberTwoDecimal, toNumber } from '../../utils/number.util';
import TooltipCircle from '../../assets/TooltipCircle';
import { Tooltip } from 'react-tooltip';
import { OrderInterface } from '../OrdersTable/interfaces/Order.interface';
import { TruckDto } from '../../services/truck.services';
import BankTransfer from '../../assets/BankTransfer';
import { OrderTarget } from '../OrdersTable/interfaces/OrderStatus.interface';

const OrderContractDetails: React.FC<OrderContractDetailsProps> = ({
	contract,
	target,
	selectedOrder,
}) => {
	const { t } = useTranslate();
	const language = localStorage.getItem('lang') || 'en';
	const navigate = useNavigate();
	const { hideViewContractButton } = useContext(OrderDetailsContext);

	const line = contract?.lines?.[0];
	const contractQtyTons = toNumber(line?.contractQtyTons);

	const allowedOverdraftPercent = toNumber(line?.allowedOverdraftPercent);

	const overdraftedQtyTons =
		allowedOverdraftPercent > 0
			? numberTwoDecimal(contractQtyTons * (1 + allowedOverdraftPercent / 100))
			: 0;

	const remainingQtyTons = useMemo(() => {
		const contractQty = toNumber(line?.contractQtyTons);
		const contractOrders = contract?.orders ?? [];

		const getExecutedQtyForOrder = (order: OrderInterface) => {
			const trucks = order?.trucks ?? [];

			if (trucks.length > 1) {
				return trucks.reduce(
					(sum: number, tr: TruckDto) =>
						sum + toNumber(tr?.loadedQuantity ?? tr?.payload),
					0,
				);
			}

			if (trucks.length === 1) {
				const tr = trucks[0];
				const hasLoaded =
					tr?.loadedQuantity !== undefined && tr?.loadedQuantity !== null;

				return hasLoaded
					? toNumber(tr.loadedQuantity)
					: toNumber(order?.quantity);
			}

			return toNumber(order?.quantity);
		};

		const executedQtyTotal = contractOrders.reduce(
			(sum: number, order: OrderInterface) =>
				sum + getExecutedQtyForOrder(order),
			0,
		);

		return contractQty - executedQtyTotal;
	}, [contract?.orders, line?.contractQtyTons]);

	const basePricePerTon =
		line?.pricePerTonSeller ?? line?.pricePerTonBuyer ?? 0;
	const sellerPricePerTon = line?.pricePerTonSeller ?? 0;

	const markupPercent = line?.markup ?? 0;

	const buyerPriceWithMarkup =
		markupPercent && basePricePerTon
			? basePricePerTon * (1 + markupPercent / 100)
			: line?.pricePerTonBuyer ?? basePricePerTon;

	return (
		<>
			<div className="flex flex-col gap-6">
				<div className="flex items-start gap-3">
					<AdminContracts width={30} height={30} />

					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-80 text-[24px] leading-[32px]">
									<T keyName="orderContractDetails.executionByContract">
										Execution by contract
									</T>
								</p>
							</SypacText>

							{!hideViewContractButton && (
								<SypacButton variant="secondary" size="small">
									<a
										href={
											contract?.id ? `/contracts/${contract.id}` : '/contracts'
										}
										className="w-full border-none bg-transparent p-0"
										onClick={(e) => {
											e.preventDefault();
											const id = contract?.id ?? selectedOrder?.contractId;
											if (id) {
												navigate(`/contracts/${id}`, {
													state: { openContractId: Number(id) },
												});
											} else {
												navigate('/contracts');
											}
										}}
									>
										<SypacText variant="body-normal-medium">
											<p className="transition text-gray-60 hover:text-gray-80 hover:-translate-y-[3px] text-[16px] leading-[24px]">
												<T keyName="orderContractDetails.viewContract">
													View contract
												</T>
											</p>
										</SypacText>
									</a>
								</SypacButton>
							)}
						</div>

						<SypacText variant="body-regular-medium">
							<p className="text-gray-40 text-[14px] leading-[16px]">
								<T keyName="orderContractDetails.thisOrderExecutedUnderContract">
									This order is executed under a contract. All terms and details
									of the transaction are governed by the agreement between the
									parties.
								</T>
							</p>
						</SypacText>
					</div>
				</div>

				<div className="flex flex-col gap-4 p-4 bg-white border border-solid border-gray-10 rounded-[12px]">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-60 text-[16px] leading-[22px]">
							<T keyName="generalTab.contractInfo">Contract Info</T>
						</p>
					</SypacText>

					<div className="flex flex-col gap-3">
						<div className="grid grid-cols-[220px,auto] items-start">
							<div className="flex items-center gap-3">
								<Person width={16} height={20} />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.buyer">Buyer</T>
									</p>
								</SypacText>
							</div>

							<div className="flex flex-col gap-2">
								<SypacText variant="body-regular-medium">
									<p className="text-cornflower-blue">
										{contract?.buyer?.name || ''}
									</p>
								</SypacText>

								{line?.ignoreCreditLimit && (
									<div className="flex gap-2">
										<BankTransfer color="#454545" width={20} height={20} />

										<div className="flex items-center gap-3">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-80">
													<T keyName="generalTab.creditLimitDisabled">
														Credit limit disabled
													</T>
												</p>
											</SypacText>
											<span
												data-tooltip-id="order-contract-details"
												data-tooltip-content={
													target !== OrderTarget.customer
														? t(
																'generalTab.creditLimitTooltip',
																'Orders created from this contract',
														  )
														: t(
																'generalTab.creditLimitTooltipBuyer',
																'Orders you place from this contract',
														  )
												}
												className="p-0 m-0 mb-[2px] flex scale-[1.1]"
											>
												<TooltipCircle />
											</span>
										</div>
									</div>
								)}
							</div>
						</div>
						<div className="grid grid-cols-[220px,auto] items-start">
							<div className="flex items-center gap-3">
								<ProducerCompanyIcon width={20} height={22} />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.seller">Seller</T>
									</p>
								</SypacText>
							</div>

							<SypacText variant="body-regular-medium">
								<p className="text-cornflower-blue">
									{contract?.seller?.name || ''}
								</p>
							</SypacText>
						</div>
						<DashedDivider dashLength={5} gapLength={5} />

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.productName">Product name:</T>
								</p>
							</SypacText>

							<div className="flex flex-col gap-3">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										{contract?.lines?.[0]?.product?.translations?.find(
											(locale) => locale.language === language,
										)?.name || contract?.lines?.[0]?.product?.name}
									</p>
								</SypacText>

								{contract?.lines?.[0]?.product.productBase ? (
									<div className="flex items-start gap-3">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												{contract?.lines?.[0]?.product.productBase.name || ''}
											</p>
										</SypacText>
										<div className="w-[2px] h-[2px] bg-gray-40 rounded-full mt-[6px]" />
										<div className="flex items-start gap-1">
											{contract?.lines?.[0]?.product.productBase.countryCode ? (
												<span>
													<Flag
														className="object-cover border border-solid border-gray-10 rounded"
														code={
															contract?.lines?.[0]?.product.productBase
																.countryCode
														}
														width={20}
														height={14}
													/>
												</span>
											) : null}
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px]">
													{contract?.lines?.[0]?.product.productBase.address ||
														''}
												</p>
											</SypacText>
										</div>
									</div>
								) : null}
							</div>
						</div>
						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.productType">Product type:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									{`${contract?.lines?.[0]?.product?.type || ''}${
										contract?.lines?.[0]?.product?.size
											? ` (${contract?.lines?.[0]?.product?.size})`
											: ''
									}`}
								</p>
							</SypacText>
						</div>

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.pricePerTon">Price per ton:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										value={
											target === OrderTarget.producer
												? numberTwoDecimal(basePricePerTon) || 0
												: numberTwoDecimal(buyerPriceWithMarkup) || 0
										}
										suffix=" PLN"
									/>
								</p>
							</SypacText>
						</div>

						{target === OrderTarget.admin && (
							<div className="grid grid-cols-[220px,auto] items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.pricePerTonSeller">
											Price per ton (Seller):
										</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={numberTwoDecimal(sellerPricePerTon) || 0}
											suffix=" PLN"
										/>
									</p>
								</SypacText>
							</div>
						)}

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.contractQuantity">
										Contract quantity:
									</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										value={contract?.lines?.[0]?.contractQtyTons || 0}
									/>{' '}
									<T keyName="basicDetails.tons">tons</T>
								</p>
							</SypacText>
						</div>

						{allowedOverdraftPercent > 0 && (
							<div className="grid grid-cols-[220px,auto] items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.overdraftedQuantity">
											Overdrafted quantity:
										</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={overdraftedQtyTons}
										/>{' '}
										<T keyName="basicDetails.tons">tons</T>
									</p>
								</SypacText>
							</div>
						)}

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.orderedQuantity">Ordered quantity:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										value={numberTwoDecimal(selectedOrder?.quantity!) || 0}
									/>{' '}
									<T keyName="basicDetails.tons">tons</T>
								</p>
							</SypacText>
						</div>

						{remainingQtyTons > 0 && (
							<div className="grid grid-cols-[220px,auto] items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.remainingQuantity">
											Remaining quantity:
										</T>
									</p>
								</SypacText>

								<div className="flex items-center gap-3">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={numberTwoDecimal(remainingQtyTons)}
											/>{' '}
											<T keyName="basicDetails.tons">tons</T>
										</p>
									</SypacText>
									<span
										data-tooltip-id="order-contract-details"
										data-tooltip-content={t(
											'orderContractDetails.remainingQuantityTooltip',
											'Remaining quantity is calculated as:',
										)}
										className="p-0 m-0 mb-[2px] flex scale-[1.1]"
									>
										<TooltipCircle />
									</span>
								</div>
							</div>
						)}

						<DashedDivider dashLength={5} gapLength={5} />

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.contractDateFrom">
										Contract date from:
									</T>
								</p>
							</SypacText>

							<div className="flex items-center gap-3 ml-[5px]">
								<span className="p-0 m-0 flex scale-[1.1]">
									<Calendar />
								</span>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										{formatDate(contract?.startsAt)}
									</p>
								</SypacText>
							</div>
						</div>
						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.contractDateTo">Contract date to:</T>
								</p>
							</SypacText>

							<div className="flex items-center gap-3 ml-[5px]">
								<span className="p-0 m-0 flex scale-[1.1]">
									<Calendar />
								</span>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										{formatDate(contract?.expiresAt)}
									</p>
								</SypacText>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Tooltip
				place="top"
				id="order-contract-details"
				style={{
					backgroundColor: '#E8E8E8',
					color: '#000000',
					whiteSpace: 'normal',
					maxWidth: '248px',
				}}
			/>
		</>
	);
};

export default OrderContractDetails;
